import {Component, OnInit, effect, ChangeDetectorRef} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';

import { Carts } from '../../../core/services/carts.service';
import { Auth } from '../../../core/services/auth.service';
import { OrderService } from '../../../core/services/order.service';
import { TipoEnvioService } from '../../../core/services/tipo-envio.service';
import { UbigeoService, UbigeoItem } from '../../../core/services/ubigeo.service';
import { TipoEnvio } from '../../../shared/models/system';

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [
    CommonModule, FormsModule, InputTextModule,
    SelectModule, RadioButtonModule, ToastModule,
    ButtonModule
  ],
  providers: [MessageService],
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css']
})
export class CheckoutComponent implements OnInit {

  orderData = {
    clientName: '',
    email: '',
    clientPhone: '',
    dni: '',
    district: '',
    city: '',
    deliveryAddress: '',
    reference: ''
  };

  deliveryCost: number = 0;
  tipoEnvioId: number | null = null;
  tiposEnvio: TipoEnvio[] = [];

  paises: UbigeoItem[] = [];
  departamentos: UbigeoItem[] = [];
  provincias: UbigeoItem[] = [];
  distritosUbigeo: UbigeoItem[] = [];
  paisId: number | null = null;
  departamentoId: number | null = null;
  provinciaId: number | null = null;
  distritoId: number | null = null;

  isLoading: boolean = false;

  savedAddress: {
    distritoId?: number;
    distritoNombre?: string;
    direccion?: string;
    label?: string;
    departamentoNombre?: string;
  } | null = null;
  useSavedAddress = false;

  public isOrderFinished: boolean = false;

  constructor(
    public cartService: Carts,
    private auth: Auth,
    private router: Router,
    private cd: ChangeDetectorRef,
    private messageService: MessageService,
    private orderService: OrderService,
    private tipoEnvioService: TipoEnvioService,
    private ubigeoService: UbigeoService
  ) {
    effect(() => {
      if (this.cartService.cartItems().length === 0 && !this.isLoading && !this.isOrderFinished) {
        this.router.navigate(['/shop']);
      }
    });
  }

  get selectedTipoEnvio(): TipoEnvio | undefined {
    return this.tipoEnvioId != null ? this.tiposEnvio.find(t => t.id === this.tipoEnvioId) : undefined;
  }

  ngOnInit(): void {
    if (!this.auth.isLoggedIn()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Inicia sesion',
        detail: 'Debes iniciar sesion para completar tu compra'
      });
      this.router.navigate(['/shop']);
      return;
    }
    this.cartService.refreshCart();
    this.loadUserData();
    this.loadTiposEnvio();
    this.loadPaises();
  }

  private isPublicDeliveryTipo(t: TipoEnvio): boolean {
    const n = (t.nombreTipoEnvio || '').toLowerCase();
    if (n.includes('recojo') || n.includes('encuentro')) return false;
    return n.includes('delivery') || n.includes('domicilio') || Boolean(t.isNational);
  }

  loadTiposEnvio() {
    this.tipoEnvioService.getActive().subscribe({
      next: (list) => {
        this.tiposEnvio = (list || []).filter(t => this.isPublicDeliveryTipo(t));
        if (this.tiposEnvio.length && this.tipoEnvioId == null) {
          this.tipoEnvioId = this.tiposEnvio[0].id;
          this.applyTipoEnvioPrecio();
        }
        this.cd.detectChanges();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Envio', detail: 'No se pudieron cargar los tipos de envio' });
      }
    });
  }

  loadPaises() {
    this.ubigeoService.getPaises().subscribe({
      next: (list) => {
        this.paises = list || [];
        if (this.paises.length && !this.useSavedAddress) {
          setTimeout(() => this.setDefaultUbigeoLima(), 0);
        }
        this.cd.detectChanges();
      },
      error: () => {}
    });
  }

  private setDefaultUbigeoLima(): void {
    const peru = this.paises.find(p => {
      const n = (p.nombre || '').toLowerCase();
      return n.includes('peru') || n.includes('perú');
    });
    if (!peru) return;
    this.paisId = peru.id;
    this.ubigeoService.getDepartamentos(peru.id).subscribe({
      next: (depts) => {
        this.departamentos = depts;
        const lima = depts.find(d => (d.nombre || '').toLowerCase() === 'lima');
        if (lima) {
          this.departamentoId = lima.id;
          this.orderData.city = lima.nombre;
          this.ubigeoService.getProvincias(lima.id).subscribe({
            next: (provs) => {
              this.provincias = provs;
              const limaProv = provs.find(p => (p.nombre || '').toLowerCase() === 'lima');
              if (limaProv) {
                this.provinciaId = limaProv.id;
                this.ubigeoService.getDistritos(limaProv.id).subscribe({
                  next: (distritos) => {
                    this.distritosUbigeo = distritos;
                    this.cd.detectChanges();
                  }
                });
              } else this.cd.detectChanges();
            }
          });
        } else this.cd.detectChanges();
      }
    });
  }

  onTipoEnvioChange() {
    this.applyTipoEnvioPrecio();
  }

  private applyTipoEnvioPrecio() {
    const t = this.selectedTipoEnvio;
    this.deliveryCost = t?.precio != null ? Number(t.precio) : 0;
  }

  onPaisChange(): void {
    this.departamentoId = null;
    this.provinciaId = null;
    this.distritoId = null;
    this.provincias = [];
    this.distritosUbigeo = [];
    this.orderData.district = '';
    this.orderData.city = '';
    if (this.paisId) {
      this.ubigeoService.getDepartamentos(this.paisId).subscribe({
        next: (d) => { this.departamentos = d; this.cd.detectChanges(); }
      });
    } else this.departamentos = [];
  }

  onDepartamentoChange(): void {
    this.provinciaId = null;
    this.distritoId = null;
    this.distritosUbigeo = [];
    this.orderData.district = '';
    const dep = this.departamentos.find(d => d.id === this.departamentoId);
    this.orderData.city = dep?.nombre || '';
    if (this.departamentoId) {
      this.ubigeoService.getProvincias(this.departamentoId).subscribe({
        next: (p) => { this.provincias = p; this.cd.detectChanges(); }
      });
    } else this.provincias = [];
  }

  onProvinciaChange(): void {
    this.distritoId = null;
    this.orderData.district = '';
    if (this.provinciaId) {
      this.ubigeoService.getDistritos(this.provinciaId).subscribe({
        next: (d) => { this.distritosUbigeo = d; this.cd.detectChanges(); }
      });
    } else this.distritosUbigeo = [];
  }

  onDistritoUbigeoChange(): void {
    const d = this.distritosUbigeo.find(x => x.id === this.distritoId);
    this.orderData.district = d?.nombre || '';
    this.cd.detectChanges();
  }

  loadUserData() {
    this.auth.getUserInfoFromToken().subscribe({
      next: (user: any) => {
        this.orderData.clientName = user.fullname || user.username || '';
        this.orderData.email = user.email || '';
        this.orderData.clientPhone = user.telephone || user.phoneNumber || '';
        if (user.distritoId || user.direccion) {
          const parts = [user.direccion, user.distritoNombre, user.departamentoNombre].filter(Boolean);
          this.savedAddress = {
            distritoId: user.distritoId,
            distritoNombre: user.distritoNombre,
            direccion: user.direccion,
            departamentoNombre: user.departamentoNombre,
            label: parts.join(', ')
          };
          this.useSavedAddress = true;
          this.applySavedAddress();
        }
        this.cd.detectChanges();
      },
      error: (err) => { console.error(err); }
    });
  }

  applySavedAddress() {
    if (!this.savedAddress) return;
    this.orderData.deliveryAddress = this.savedAddress.direccion || '';
    this.orderData.district = this.savedAddress.distritoNombre || '';
    this.orderData.city = this.savedAddress.departamentoNombre || '';
    this.distritoId = this.savedAddress.distritoId ?? null;
    this.applyTipoEnvioPrecio();
    if (this.savedAddress.distritoId) {
      this.ubigeoService.getDistritoConPadres(this.savedAddress.distritoId).subscribe({
        next: (dp) => {
          this.paisId = dp.paisId;
          this.departamentoId = dp.departamentoId;
          this.provinciaId = dp.provinciaId;
          this.distritoId = dp.id;
          this.ubigeoService.getDepartamentos(dp.paisId).subscribe({
            next: (d) => { this.departamentos = d; this.cd.detectChanges(); }
          });
          this.ubigeoService.getProvincias(dp.departamentoId).subscribe({
            next: (p) => { this.provincias = p; this.cd.detectChanges(); }
          });
          this.ubigeoService.getDistritos(dp.provinciaId).subscribe({
            next: (list) => { this.distritosUbigeo = list; this.cd.detectChanges(); }
          });
        },
        error: () => {}
      });
    }
  }

  onAddressModeChange(useSaved: boolean) {
    this.useSavedAddress = useSaved;
    if (useSaved) {
      this.applySavedAddress();
    } else {
      this.distritoId = null;
      this.orderData.deliveryAddress = '';
      this.orderData.district = '';
      this.applyTipoEnvioPrecio();
      if (this.paises.length) this.setDefaultUbigeoLima();
    }
    this.cd.detectChanges();
  }

  payWithMercadoPago() {
    if (!this.orderData.clientName || !this.orderData.dni || !this.orderData.clientPhone) {
      this.messageService.add({severity:'warn', summary:'Faltan datos', detail:'Completa tus datos de contacto'});
      return;
    }
    if (!this.tipoEnvioId) {
      this.messageService.add({severity:'warn', summary:'Envio', detail:'Selecciona un tipo de envio'});
      return;
    }
    if (this.useSavedAddress && this.savedAddress) {
      if (!this.orderData.deliveryAddress && !this.orderData.district) {
        this.messageService.add({severity:'warn', summary:'Envio', detail:'Tu direccion guardada esta incompleta'});
        return;
      }
    } else if (!this.distritoId || !this.orderData.deliveryAddress) {
      this.messageService.add({severity:'warn', summary:'Envio', detail:'Selecciona distrito y direccion'});
      return;
    }

    this.isLoading = true;
    this.isOrderFinished = true;

    const finalOrder = {
      clientName: this.orderData.clientName,
      email: this.orderData.email,
      clientPhone: this.orderData.clientPhone,
      dni: this.orderData.dni,
      city: this.orderData.city,
      district: this.orderData.district,
      deliveryAddress: this.orderData.deliveryAddress,
      reference: this.orderData.reference,
      tipoEnvioId: this.tipoEnvioId,
      distritoId: this.distritoId,
      deliveryPrice: this.deliveryCost,
      items: this.cartService.cartItems().map((item: any) => ({
        productId: item.productId,
        quantity: item.quantity,
        size: item.selectedSize || item.size,
        color: item.selectedColor || item.color,
        unitPrice: item.unitPrice ?? item.productPrice,
        subTotal: item.subTotal
      }))
    };

    this.orderService.createMercadoPagoCheckout(finalOrder).subscribe({
      next: (res) => {
        const redirectUrl = res.initPoint || res.sandboxInitPoint;
        if (!redirectUrl) {
          this.isLoading = false;
          this.isOrderFinished = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Pago',
            detail: 'No se recibio URL de pago de MercadoPago'
          });
          return;
        }
        window.location.href = redirectUrl;
      },
      error: (err) => {
        this.isLoading = false;
        this.isOrderFinished = false;
        console.error(err);
        const detail = err?.error?.message || 'No se pudo iniciar el pago con MercadoPago';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail
        });
      }
    });
  }
}